import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { getSignerTitle } from "@/pages/esign/esign.config.ts";
import { useEsignReminder } from "@/pages/esign/use-esign-reminder.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { EsignDocumentPreviewModal } from "@/pages/esign/widgets/EsignDocumentPreviewModal.tsx";
import { EsignRequestSentModal } from "@/pages/esign/widgets/EsignRequestSentModal.tsx";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { useEmployeeDocumentsFolders } from "@/queries/employees/use-employee-documents.query.ts";
import {
  useEsignEnvelopeCreate,
  useEsignEnvelopePreview,
  useEsignMarkup,
} from "@/queries/use-esign.query.ts";
import useEmployeeSearchInline, {
  useEmployeeMe,
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useRuleGroupsPreview, useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { ApiFile } from "@/types/common.types.ts";
import { ApiDocumentPreview } from "@/types/document.types.ts";
import {
  ApiEsignEnvelopeSave,
  ApiEsignMarkupField,
  EsignFieldFilledBy,
  EsignFieldType,
  EsignSignatureType,
} from "@/types/esign.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import {
  Rule,
  RuleApplySignType,
  RuleConditionType,
  RuleShort,
  RuleGroup,
  RuleGroupShort,
} from "@/types/rule.types.ts";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  LinkButtonTo,
  ListItem,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Skeleton,
  Stack,
  Text,
  Textarea,
  useModals,
} from "@skailer-com/uikit";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface EsignRequestModalProps extends ModalProps {
  documentId: number;
  documentTitle: string;
  file?: ApiFile;
  fileName: string;
  preview: ApiDocumentPreview;
  signatureType: EsignSignatureType;
  isEmployeeDocument?: boolean;
  sourceFolderId?: number;
  sourceFolderTitle?: string;
  defaultRecipient?: SelectItem;
}

const STORE_REPLACE = "replace";

const defaultRecipientGroup: Rule = {
  condition_type: RuleConditionType.all_active_employees,
  sign: RuleApplySignType.include,
  condition_ids: [],
  condition_entities: [],
  value_entities: [],
};

function toRuleShort(rule: Rule): RuleShort {
  return {
    condition_type: rule.condition_type,
    sign: rule.sign,
    condition_ids: rule.condition_ids,
    value_ids: rule.value_ids,
    date_condition: rule.date_condition,
  };
}

function useEnvelopePreviewForRules(
  documentId: number,
  recipientRules: RuleGroupShort[],
  enabled: boolean
) {
  const { mutate, reset, data, isPending } = useEsignEnvelopePreview();

  useEffect(() => {
    if (!enabled || recipientRules.length === 0) {
      reset();
      return;
    }
    mutate({ document_id: documentId, recipient_rule_groups: recipientRules });
  }, [recipientRules, documentId, enabled, mutate, reset]);

  return {
    data: enabled ? data : undefined,
    isLoading: enabled && recipientRules.length > 0 && isPending,
  };
}

interface SenderFieldControlProps {
  field: ApiEsignMarkupField & { id: number };
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

function SenderFieldControl({ field, value, onChange, error }: SenderFieldControlProps) {
  const { t } = useTranslation();
  const label = field.label ?? undefined;

  switch (field.field_type) {
    case EsignFieldType.yes_no:
      return (
        <Select
          label={label}
          emptyTitle={t("common:select")}
          required={field.is_required}
          options={[
            { id: "yes", title: t("common:yes") },
            { id: "no", title: t("common:no") },
          ]}
          value={value || null}
          onChange={(next) => onChange(next != null ? String(next) : "")}
          error={error}
        />
      );
    case EsignFieldType.text:
      return field.is_multiline ? (
        <Textarea
          label={label}
          required={field.is_required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={error}
        />
      ) : (
        <Input
          label={label}
          required={field.is_required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={error}
        />
      );
    default:
      return null;
  }
}

export function EsignRequestModal({
  documentId,
  documentTitle,
  file,
  fileName,
  preview,
  signatureType,
  isEmployeeDocument,
  sourceFolderId,
  sourceFolderTitle,
  defaultRecipient,
  ...props
}: EsignRequestModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { onError: handleApiError } = useErrorHandle();
  const reminder = useEsignReminder();
  const { mutate: createEnvelope, isPending } = useEsignEnvelopeCreate();
  const recipientConditionTypes = useRuleConditionTypes(RuleRegistryPlace.esignRecipients);
  const { id } = props;
  const isEmployeeDoc = !!isEmployeeDocument;

  const [isMultiple, setIsMultiple] = useState(false);
  const [recipientId, setRecipientId] = useState<number | string | null>(
    defaultRecipient?.id ?? null
  );
  const [recipientEntity, setRecipientEntity] = useState<SelectItem | null>(
    defaultRecipient ?? null
  );
  const { control: recipientControl, setValue: setRecipientValue } = useForm<{
    recipient_rules: RuleGroup[];
  }>({ defaultValues: { recipient_rules: [] } });
  const recipientGroups = useWatch({ control: recipientControl, name: "recipient_rules" });
  const [dateDue, setDateDue] = useState<Date | null>(null);
  const [storeValue, setStoreValue] = useState<number | string | null>(
    isEmployeeDoc ? STORE_REPLACE : null
  );
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);

  const recipientRules = useMemo<RuleGroupShort[]>(
    () =>
      isMultiple
        ? recipientGroups
            .filter((group) => group.rules.length > 0)
            .map((group) => ({ rules: group.rules.map(toRuleShort) }))
        : recipientId != null
          ? [
              {
                rules: [
                  {
                    condition_type: RuleConditionType.employee,
                    sign: RuleApplySignType.include,
                    condition_ids: [recipientId],
                  },
                ],
              },
            ]
          : [],
    [isMultiple, recipientGroups, recipientId]
  );

  const isSignature = signatureType === EsignSignatureType.signature;
  const employee = useEmployeeSearchInline(!isMultiple);
  const { total: recipientCount } = useRuleGroupsPreview(
    recipientGroups,
    RuleRegistryPlace.esignRecipients,
    isMultiple
  );
  const { data: markup } = useEsignMarkup(documentId);
  const { data: employeeMe } = useEmployeeMe();
  const { data: employeeFolders } = useEmployeeDocumentsFolders(employeeMe?.id);
  const folders = employeeFolders?.filter((folder) => folder.acl.can_edit_documents);

  const senderFields = useMemo(
    () =>
      (isSignature ? (markup?.roles ?? []) : [])
        .flatMap((role) => role.fields)
        .filter(
          (field): field is ApiEsignMarkupField & { id: number } =>
            field.filled_by === EsignFieldFilledBy.sender && field.id != null
        ),
    [markup, isSignature]
  );

  const {
    control: fieldValuesControl,
    reset: resetFieldValues,
    trigger: triggerFieldValues,
    getValues: getFieldValues,
    clearErrors: clearFieldErrors,
    formState: { errors: fieldValuesErrors },
  } = useForm<{ field_values: { field_id: number; value: string }[] }>({
    defaultValues: { field_values: [] },
  });
  const { fields: fieldValueControls } = useFieldArray({
    control: fieldValuesControl,
    name: "field_values",
  });
  const fieldValuesInitedRef = useRef(false);

  useEffect(() => {
    if (fieldValuesInitedRef.current || markup === undefined) return;
    resetFieldValues({
      field_values: senderFields.map((field) => ({ field_id: field.id, value: "" })),
    });
    fieldValuesInitedRef.current = true;
  }, [markup, senderFields, resetFieldValues]);
  const { renderEmployeeName } = useFormatter();
  const { data: envelopePreview, isLoading: isPreviewLoading } = useEnvelopePreviewForRules(
    documentId,
    recipientRules,
    isSignature
  );
  const recipientCounts = new Map(
    envelopePreview?.roles.map((role) => [role.role_id, role.recipients_count])
  );
  const roleEmployeeIds = new Map(
    envelopePreview?.roles.map((role) => [role.role_id, role.employee_ids])
  );
  const signerEmployeeIds = useMemo(
    () => [...new Set((envelopePreview?.roles ?? []).flatMap((role) => role.employee_ids))],
    [envelopePreview]
  );
  const { data: signerEmployees, isLoading: isSignerEmployeesLoading } = useEmployeesByIds(
    signerEmployeeIds,
    isSignature && !isMultiple
  );
  const signerEmployeesById = useMemo(
    () => new Map((signerEmployees ?? []).map((employee) => [employee.id, employee])),
    [signerEmployees]
  );
  const isSignersLoading = isPreviewLoading || (!isMultiple && isSignerEmployeesLoading);

  const titleKey =
    signatureType === EsignSignatureType.acknowledge
      ? "core:esign_request_acknowledgment"
      : "core:esign_request_signatures";

  const isRecipientMissing = recipientRules.length === 0;

  const folderOptions: SelectItem[] = (folders ?? []).map((item) => ({
    id: item.id,
    title: item.title,
  }));
  const isReplaceSelected = storeValue === STORE_REPLACE;
  const selectedFolderId = !isReplaceSelected && storeValue != null ? Number(storeValue) : null;
  const resolvedFolderId = isReplaceSelected ? (sourceFolderId ?? null) : selectedFolderId;
  const selectedFolder = folders?.find((item) => item.id === selectedFolderId);
  const storeOptions: SelectItem[] = isEmployeeDoc
    ? [{ id: STORE_REPLACE, title: t("core:esign_replace_existing_document") }, ...folderOptions]
    : folderOptions;

  const totalSigners =
    envelopePreview?.roles.reduce((sum, role) => sum + role.recipients_count, 0) ?? 0;

  const openSignersPreview = (employeeIds: number[], roleTitle: string) => {
    openModal(PreviewEmployeesModal, {
      filter: { ...EmployeeFilterDefault, employee_ids: employeeIds },
      addColumns: [],
      modalTitle: roleTitle,
    });
  };

  if (!isEmployeeDoc && storeValue == null && folders?.length) {
    setStoreValue(folders[0].id);
  }

  const switchToMultiple = () => {
    setRecipientId(null);
    setRecipientEntity(null);
    setRecipientValue(
      "recipient_rules",
      recipientGroups.length > 0 ? recipientGroups : [{ rules: [defaultRecipientGroup] }]
    );
    setIsMultiple(true);
  };

  const switchToSingle = () => {
    setRecipientValue("recipient_rules", []);
    setIsMultiple(false);
  };

  const handleSubmit = async () => {
    setHasTriedSubmit(true);
    clearFieldErrors();
    if (isRecipientMissing || resolvedFolderId == null) return;
    const valuesValid = !isSignature || (await triggerFieldValues());
    if (!valuesValid) return;
    const payload: ApiEsignEnvelopeSave = {
      document_id: documentId,
      target_folder_id: resolvedFolderId,
      signature_type: signatureType,
      date_due: dateDue,
      recipient_rule_groups: recipientRules,
      ...(isSignature
        ? {
            field_values: getFieldValues("field_values").filter((item) => item.value.trim() !== ""),
          }
        : {}),
      ...reminder.buildPayload(),
    };
    if (isReplaceSelected) {
      payload.replace_source_document = true;
    }
    const acknowledgeCount = isMultiple ? (recipientCount ?? 0) : recipientId != null ? 1 : 0;
    const notifiedCount = !isSignature
      ? acknowledgeCount
      : isMultiple
        ? (envelopePreview?.envelopes_count ?? 0)
        : totalSigners;
    createEnvelope(payload, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.esign.requestSent, {
          [analyticProperties.id]: documentId,
          [analyticProperties.mode]: signatureType,
          [analyticProperties.count]: notifiedCount,
        });
        close(id);
        openModal(EsignRequestSentModal, {
          signatureType,
          recipientCount: notifiedCount,
        });
      },
      onError: (err) => handleApiError(err, fieldValuesControl),
    });
  };

  return (
    <Modal
      {...props}
      title={t(titleKey)}
      variant="side"
      layout="stretch"
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <Button
            size={"lg"}
            disabled={resolvedFolderId == null || isPending}
            isLoading={isPending}
            onClick={handleSubmit}
          >
            {t("data_protection:send_request")}
          </Button>
          <Button variant={"plain"} size={"md"} onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <ServerErrorField errors={fieldValuesErrors} />
        <Stack gap={"xs"}>
          <Text className={"title-sub-headline"}>{t("core:recipient")}</Text>

          {isMultiple ? (
            <Stack gap={"sm"}>
              <RuleGroupsEditor
                control={recipientControl}
                setValue={setRecipientValue}
                fieldName="recipient_rules"
                place={RuleRegistryPlace.esignRecipients}
                conditionTypes={recipientConditionTypes.conditionTypes}
                variant="list"
              />

              <Stack direction={"horizontal"} gap={"xs"} items={"center"} justify={"center"}>
                <Text className={"text-secondary"} size="xs">
                  {t("core:esign_return_to")}
                </Text>
                <LinkButtonTo variant={"secondary"} size={"sm"} onClick={switchToSingle}>
                  {t("core:esign_single_recipient")}
                </LinkButtonTo>
              </Stack>
            </Stack>
          ) : (
            <>
              <Select
                emptyTitle={t("core:esign_choose_recipient")}
                required={true}
                error={
                  hasTriedSubmit && isRecipientMissing
                    ? t("core:esign_recipient_required")
                    : undefined
                }
                searchable={true}
                showAvatar={true}
                showCaption={true}
                loadOptionsOnOpen={true}
                options={recipientEntity ? [recipientEntity] : undefined}
                onSearchOptions={employee.search}
                searchOptions={employee.options}
                value={recipientId}
                onChange={(value, entity) => {
                  setRecipientId(value ?? null);
                  setRecipientEntity(entity ?? null);
                }}
              />
              <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                <Text className={"text-secondary"} size="xs">
                  {t("core:esign_send_to")}
                </Text>
                <LinkButtonTo variant={"secondary"} size={"sm"} onClick={switchToMultiple}>
                  {t("core:esign_multiple_recipients")}
                </LinkButtonTo>
              </Stack>
            </>
          )}

          {isMultiple && hasTriedSubmit && isRecipientMissing && (
            <Text className={"text-danger"} size={"xs"}>
              {t("core:esign_recipient_required")}
            </Text>
          )}
        </Stack>

        <Stack gap={"xs"}>
          <Text className={"title-sub-headline"}>{t("core:document")}</Text>
          <ListItem
            icon={
              <Avatar size={"md"} icon={<FontAwesomeIcon icon={"fa-light fa-file-contract"} />} />
            }
            title={documentTitle}
            caption={fileName}
            valueSlot={
              <Button
                variant={"secondary"}
                size={"sm"}
                className={"shrink-0 whitespace-nowrap"}
                disabled={!file}
                onClick={() => {
                  if (!file) return;
                  openModal(EsignDocumentPreviewModal, {
                    documentId,
                    file,
                    fileName,
                    preview,
                    showFields: signatureType === EsignSignatureType.signature,
                  });
                }}
              >
                {t("common:preview")}
              </Button>
            }
          />
        </Stack>

        {signatureType === EsignSignatureType.signature && (
          <Stack gap={"xs"}>
            <Text className={"title-sub-headline"}>{t("core:esign_signers")}</Text>
            {isSignersLoading ? (
              <Stack>
                {Array.from({ length: markup?.roles.length || 1 }).map((_, index) => (
                  <Stack
                    key={`signer-skeleton-${index}`}
                    direction={"horizontal"}
                    items={"center"}
                    gap={"md"}
                    className={"border-b border-dark/5 py-2 last:border-b-0"}
                  >
                    <Skeleton className={"h-9 w-9 rounded-sm"} />
                    <Stack gap={"xs"} className={"flex-1"}>
                      <Skeleton className={"h-4 w-32"} />
                      <Skeleton className={"h-3 w-20"} />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : isMultiple ? (
              <Stack>
                {markup?.roles.map((role, index) => {
                  const title = getSignerTitle(role, t);
                  const employeeIds = role.id != null ? (roleEmployeeIds.get(role.id) ?? []) : [];
                  const count = employeeIds.length;
                  return (
                    <Stack
                      key={`signer-${role.id ?? index}`}
                      direction={"horizontal"}
                      items={"center"}
                      gap={"md"}
                      className={"border-b border-dark/5 py-2 last:border-b-0"}
                    >
                      <Avatar acronym={title} />
                      <Stack className={"min-w-0 flex-1"}>
                        <Text className={"text-title"}>{title}</Text>
                        <Text className={"text-secondary"}>
                          {t("core:esign_recipients_count", { count })}
                        </Text>
                      </Stack>
                      {count > 0 && (
                        <Text
                          className={
                            "underline text-dark/95 cursor-pointer whitespace-nowrap hover:no-underline"
                          }
                          tabIndex={0}
                          onClick={() => openSignersPreview(employeeIds, title)}
                        >
                          {t("common:view_all")}
                        </Text>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Stack>
                {markup?.roles.map((role, index) => {
                  const title = getSignerTitle(role, t);
                  const count = role.id != null ? recipientCounts.get(role.id) : undefined;
                  const employees =
                    role.id != null
                      ? (roleEmployeeIds.get(role.id) ?? [])
                          .map((employeeId) => signerEmployeesById.get(employeeId))
                          .filter((employee) => employee != null)
                      : [];

                  if (employees.length === 0) {
                    return (
                      <Stack
                        key={`signer-${role.id ?? index}`}
                        direction={"horizontal"}
                        items={"center"}
                        gap={"md"}
                        className={"border-b border-dark/5 py-2 last:border-b-0"}
                      >
                        <Avatar acronym={title} />
                        <Stack className={"min-w-0"}>
                          <Text className={"text-title"}>{title}</Text>
                          <Text className={"text-secondary"}>
                            {count != null
                              ? t("core:esign_recipients_count", { count })
                              : t("core:esign_select_recipient_first")}
                          </Text>
                        </Stack>
                      </Stack>
                    );
                  }

                  return employees.map((employee) => (
                    <Stack
                      key={`signer-${role.id ?? index}-${employee.id}`}
                      direction={"horizontal"}
                      items={"center"}
                      gap={"md"}
                      className={"border-b border-dark/5 py-2 last:border-b-0"}
                    >
                      <EmployeeAvatar employee={employee} />
                      <Stack className={"min-w-0"}>
                        <Text className={"text-title"}>{renderEmployeeName(employee)}</Text>
                        <Text className={"text-secondary"}>{title}</Text>
                      </Stack>
                    </Stack>
                  ));
                })}
              </Stack>
            )}
          </Stack>
        )}

        {senderFields.length > 0 && (
          <Stack gap={"xs"}>
            <Text className={"title-sub-headline"}>{t("core:esign_fields_to_fill")}</Text>
            <Text className={"text-secondary"} size={"xs"}>
              {t("core:esign_sender_fields_shared_hint")}
            </Text>
            <Stack gap={"sm"}>
              {fieldValueControls.map((fieldControl, index) => {
                const field = senderFields[index];
                if (!field) return null;
                const error = fieldValuesErrors.field_values?.[index]?.value?.message;
                return (
                  <Controller
                    key={fieldControl.id}
                    control={fieldValuesControl}
                    name={`field_values.${index}.value`}
                    rules={field.is_required ? { required: t("errors:field_required") } : undefined}
                    render={({ field: controlField }) => (
                      <SenderFieldControl
                        field={field}
                        value={controlField.value}
                        onChange={controlField.onChange}
                        error={error}
                      />
                    )}
                  />
                );
              })}
            </Stack>
          </Stack>
        )}

        <Stack gap={"xs"}>
          <Text className={"title-sub-headline"}>{t("core:esign_reminders")}</Text>
          <Stack direction={"horizontal"} gap={"sm"}>
            <Box className={"flex-1"}>
              <Datepicker
                value={dateDue}
                onChange={setDateDue}
                label={t("core:esign_select_due_date")}
                minDate={new Date()}
                isClearable={true}
              />
            </Box>
            <Box className={"flex-1"}>
              <Select
                emptyTitle={t("core:esign_reminder_frequency")}
                showCaption={true}
                captionPerItem={true}
                options={reminder.options}
                value={reminder.value}
                onChange={reminder.onChange}
              />
            </Box>
          </Stack>
        </Stack>

        <Stack gap={"xs"}>
          <Text className={"title-sub-headline"}>{t("core:esign_store_signed_document")}</Text>
          <Select
            label={t("core:folder")}
            emptyTitle={t("core:folder")}
            options={storeOptions}
            value={storeValue}
            onChange={(value) => setStoreValue(value ?? null)}
          />
          {isReplaceSelected ? (
            <>
              <Text className={"text-secondary"} size={"xs"}>
                {t("core:esign_replace_source_hint")}
              </Text>
              {sourceFolderTitle && (
                <Text className={"text-secondary"} size={"xs"}>
                  {`${t("core:esign_store_path_profile")} → ${t("core:esign_store_path_docs")} → ${sourceFolderTitle}`}
                </Text>
              )}
            </>
          ) : selectedFolder ? (
            <Text className={"text-secondary"} size={"xs"}>
              {`${t("core:esign_store_path_profile")} → ${t("core:esign_store_path_docs")} → ${selectedFolder.title}`}
            </Text>
          ) : folders && storeOptions.length === 0 ? (
            <Text className={"text-secondary"} size={"xs"}>
              {t("core:esign_no_store_folders")}
            </Text>
          ) : null}
        </Stack>
      </Stack>
    </Modal>
  );
}
